<template>
  <CustomModal id="chapter-edit-modal"
               class="basic-modal chapter-edit-modal"
               v-bind:ok-disabled="saveDisabled"
               v-bind:ok-busy="busy"
               v-bind:ok-title="$t('common.save')"
               v-on:cancel="$emit('cancel')"
               v-on:ok="save()">
    <div class="chapter-edit-modal__title" slot="modal-header">
      {{ modalTitle }}
    </div>

    <div class="text-center" v-if="loading">
      <LoadingBounce></LoadingBounce>
      <div class="typography--body-2 typography--color-disabled pb-5">
        {{ $t('common.loading') }}
      </div>
    </div>

    <div class="chapter-edit-modal__form" v-else>
      <div class="form-group">
        <label for="chapter-title-input"
              class="typography--body-3 typography--color-secondary typography--font-weight-600">
          {{ $t('publishers.modal.chapter_edit.name') }}
        </label>
        <input 
          type="text"
          id="chapter-title-input"
          class="form-control"
          v-bind:placeholder="$t('publishers.modal.chapter_edit.name_placeholder')"
          v-model="chapterTitle"
        >
      </div>
      
      <div class="form-group">
        <label for="domain-select"
               class="typography--body-3 typography--color-secondary typography--font-weight-600">
          {{ $t('publishers.modal.chapter_edit.domain') }}
        </label>
        <select v-model="selected.domainId"
                id="domain-select"
                class="form-control"
                v-bind:disabled="filteredDomains.length === 0">
          <option disabled v-bind:value="null">
            {{ $t('publishers.modal.chapter_edit.domain_placeholder') }}
          </option>
          <option v-for="domain in filteredDomains"
                  v-bind:key="domain.id"
                  v-bind:value="domain.id">
            {{ domain.name }}
          </option>
        </select>
      </div>

      <div class="form-group">
        <label for="core-concept-select"
              class="typography--body-3 typography--color-secondary typography--font-weight-600">
          {{ $t('publishers.modal.chapter_edit.core_concept') }}
        </label>
        <select v-model="selected.coreConceptId"
                id="core-concept-select"
                class="form-control"
                v-bind:disabled="filteredCoreConepts.length === 0">
          <option disabled v-bind:value="null">
            {{ $t('publishers.modal.chapter_edit.core_concept_placeholder') }}
          </option>
          <option v-for="cc in filteredCoreConepts"
                  v-bind:key="cc.id"
                  v-bind:value="cc.id">
            {{ cc.name }} ({{ cc.description }})
          </option>
        </select>
      </div>
    </div>
  </CustomModal>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import { isNil } from 'lodash';
  import labels from '@/mixins/labels';

  export default {
    name: 'chapter-edit-modal',

    props: {
      chapter: {
        type: Object,
        default: null,
      },
      seriesItem: {
        type: Object,
        default: null,
      },
    },

    mixins: [
      labels,
    ],

    data() {
      return {
        loading: false,
        selected: {
          domainId: null,
          coreConceptId: null,
        },
        chapterTitle: '',
        filteredCoreConepts: [],
        filteredDomains: [],
        busy: false,
      };
    },

    computed: {
      ...mapGetters([
        'coreConceptList',
        'domainList',
        'subjectMap',
        'gradeMap',
      ]),

      modalTitle() {
        if (!isNil(this.chapter) && !isNil(this.seriesItem)) {
          return [
            this.getGradeLabel(this.seriesItem),
            this.getSubjectLabel(this.seriesItem),
            this.$t('series_cards.chapters.index', { i: this.chapter.position }),
          ].join(' ');
        }

        return '';
      },

      saveDisabled() {
        if (!isNil(this.chapter)) {
          return (this.chapterTitle === this.chapter.name &&
            this.selected.domainId === this.chapter.domain_id &&
            this.selected.coreConceptId === this.chapter.core_concept_id);
        }

        return true;
      },
    },

    methods: {
      ...mapActions([
        'fetchCoreConcepts',
        'fetchDomainList',
        'updateChapter',
      ]),

      save() {
        if (!this.busy) {
          this.busy = true;
  
          const params = {
            ...this.chapter,
            name: this.chapterTitle,
            domain_id: this.selected.domainId,
            core_concept_id: this.selected.coreConceptId,
          };
  
          this.updateChapter(params)
            .then(() => (
              this.$emit('ok')
            ));
        }
      },
    },

    beforeMount() {
      this.loading = true;

      if (!isNil(this.chapter) && !isNil(this.seriesItem)) {
        this.chapterTitle = this.chapter.name;
        this.selected.domainId = this.chapter.domain_id;
        this.selected.coreConceptId = this.chapter.core_concept_id;

        if (this.seriesItem.grade_id === null) {
          this.loading = false;
          return true;
        }

        return Promise.resolve()
          .then(() => {
            this.filteredDomains = this.domainList.filter(domain => (
              domain.subject_id === this.seriesItem.subject_id
            ));

            if (this.filteredDomains.length === 0) {
              return this.fetchDomainList({ subject_id: this.seriesItem.subject_id })
                .then((domains) => {
                  this.filteredDomains = domains;
                });
            }

            return true;
          })
          .then(() => {
            this.filteredCoreConepts = this.coreConceptList.filter(cc => (
              cc.subject_id === this.seriesItem.subject_id &&
              cc.grade_id === this.seriesItem.grade_id
            ));

            if (this.filteredCoreConepts.length === 0) {
              return this.fetchCoreConcepts({
                subject_id: this.seriesItem.subject_id,
                grade_id: this.seriesItem.grade_id,
              })
              .then((coreConepts) => {
                this.filteredCoreConepts = coreConepts;
              });
            }

            return true;
          })
          .then(() => {
            this.loading = false;
          });
      }

      return true;
    },
  };
</script>

<style lang="scss">
  .chapter-edit-modal {
    .chapter-edit-modal__form {
      .form-group>.form-control {
        height: 47px;
        font-size: 14px;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/app/publishers/components/ChapterEditModal.vue